/*
 * Copyright (C) 2019 SADE Innovations Oy - All Rights Reserved
 *
 * NOTICE: This software is owned by SADE Innovations Oy and licensed under SADE Booster license.
 * All dissemination, usage, modification, copying, reproduction, selling and distribution of the
 * software and its intellectual and technical concepts are strictly forbidden without a valid license.
 * Such license can be obtained by issuing a SADE Booster License agreement from SADE Innovations Oy
 * (https://sadeinnovations.com).
 */

import React, { Component, Fragment } from "react";
import Loader from "../../ui/loader";
import { Maybe } from "../../../types/aliases";
import { Button } from "@material-ui/core";
import { AuthWrapper, AuthenticatedUser, ErrorWithCode, isError } from "@sade/data-access";
import PasswordField from "../../ui/password-field";
import { translations } from "../../../generated/translationHelper";

interface Props {
  user: AuthenticatedUser;
}

interface State {
  isLoaderVisible: boolean;
  newPassword: string;
  confirmPassword: string;
  newPasswordError?: string;
  confirmPasswordError?: string;
  generalError?: string;
}

export default class CompleteAccountCreationForm extends Component<Props, State> {
  public constructor(props: Props) {
    super(props);
    this.state = {
      isLoaderVisible: false,
      newPassword: "",
      confirmPassword: "",
    };
  }

  private handleErrors(code?: string): void {
    switch (code) {
      case "UserNotLoggedIn":
        this.setState({ generalError: translations.logIn.texts.userNotLoggedIn() });
        break;
      case "Password does not conform to policy: Password must have numeric characters":
        this.setState({ newPasswordError: translations.common.texts.passwordMustHaveNumbers() });
        break;
      case "Password does not conform to policy: Password must have lowercase characters":
        this.setState({ newPasswordError: translations.common.texts.passwordMustHaveLowercaseCharacters() });
        break;
      case "1 validation error detected: Value at 'password' failed to satisfy constraint: Member must have length greater than or equal to 6":
      case "Password does not conform to policy: Password not long enough":
        this.setState({ newPasswordError: translations.common.texts.passwordMustBeLongEnough() });
        break;
      case "Invalid session for the user, session is expired.":
        this.setState({ generalError: translations.common.texts.userSessionExpired() });
        break;
      default:
        this.setState({ generalError: translations.common.texts.unableToPerformAction() });
        break;
    }
  }

  private resetErrors(): void {
    this.setState({
      generalError: undefined,
      newPasswordError: undefined,
      confirmPasswordError: undefined,
    });
  }

  private async completeAccountCreation(): Promise<void> {
    this.resetErrors();

    if (!this.passwordsMatch()) {
      this.setState({ confirmPasswordError: translations.common.texts.passwordsNotMatching() });
      return;
    }

    try {
      this.setState({ isLoaderVisible: true });
      await AuthWrapper.completeNewPassword(this.props.user, this.state.newPassword);
      const claims = await AuthWrapper.getCurrentAuthenticatedUserClaims();
      if (claims?.homeOrganizationId.includes(":")) {
        console.error("User is not a root level user, logging out");
        await AuthWrapper.logOut(translations.logIn.texts.logoutMessageNoAccess());
        throw new ErrorWithCode("UserNotFoundException");
      }
    } catch (error) {
      this.setState({ isLoaderVisible: false });
      console.error("completePassword", error);
      if (isError(error)) this.handleErrors(error.message);
    }
  }

  private validatePasswords = (): boolean => {
    return !!this.state.newPassword.length && !!this.state.confirmPassword.length;
  };

  private passwordsMatch(): boolean {
    return this.state.newPassword === this.state.confirmPassword;
  }

  private renderLoader(): Maybe<JSX.Element> {
    if (this.state.isLoaderVisible) {
      return <Loader />;
    }
  }

  private renderInputs(): JSX.Element {
    return (
      <Fragment>
        <div className="login-fields">
          <PasswordField
            label={translations.common.inputs.newPassword()}
            autoComplete="new-password"
            required={true}
            error={this.state.newPasswordError != null}
            helperText={this.state.newPasswordError}
            onChange={(newPassword: string): void => {
              this.resetErrors();
              this.setState({ newPassword });
            }}
            fullWidth={true}
            margin="normal"
            inputProps={{ "data-testid": "new-password-field" }}
          />
          <PasswordField
            label={translations.common.inputs.confirmNewPassword()}
            autoComplete="new-password"
            error={this.state.confirmPasswordError != null}
            helperText={this.state.confirmPasswordError}
            onChange={(confirmPassword: string): void => {
              this.resetErrors();
              this.setState({ confirmPassword });
            }}
            fullWidth={true}
            required={true}
            margin="normal"
            inputProps={{ "data-testid": "confirm-password-field" }}
          />
        </div>
        <div className="login-buttons">
          <Button
            disabled={!this.validatePasswords()}
            variant="contained"
            color="primary"
            onClick={(): Promise<void> => this.completeAccountCreation()}
            data-testid="new-account-login-button"
          >
            {translations.logIn.buttons.confirmLogIn()}
          </Button>
        </div>
      </Fragment>
    );
  }

  private renderGeneralError(): Maybe<JSX.Element> {
    if (this.state.generalError) {
      return (
        <div className="login-errortext" data-testid="complete-account-err">
          {translations.common.texts.errorOccurred({ error: this.state.generalError })}
        </div>
      );
    }
  }

  public render(): JSX.Element {
    return (
      <Fragment>
        <div className="login-header">{translations.logIn.texts.newPasswordRequired()}</div>
        {this.renderLoader()}
        {this.renderInputs()}
        {this.renderGeneralError()}
      </Fragment>
    );
  }
}
